import React, { useState, useEffect, memo } from 'react';
import {
  Typography,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  CircularProgress,
  Alert,
  Tooltip,
  Divider,
  useTheme,
  Snackbar
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ImportExportIcon from '@mui/icons-material/ImportExport';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import MovieIcon from '@mui/icons-material/Movie';
import { getProjectScripts } from '../../services/scriptService';
import axios from '../../utils/axiosConfig';
import { getHierarchyStyle } from '../../utils/castStyles';

// CastList component for rendering cast members by number (colors via getHierarchyStyle)
const CastList = memo(({ cast, castMembers }) => {
  const theme = useTheme();
  // Convert cast to array if it's a string, or use empty array if null/undefined
  const castArray = Array.isArray(cast) ? cast : 
    (typeof cast === 'string' ? cast.split(',').filter(Boolean).map(c => c.trim()) : []);
  
  if (castArray.length === 0) {
    return <Typography variant="body2" color="text.secondary">No cast</Typography>;
  }
  
  return castArray.map((charName, i) => {
    const castMember = castMembers.find(
      cast => cast.characterName && 
      cast.characterName.toLowerCase() === charName.toLowerCase()
    );
    // If no matching cast member, hide the placeholder (do not render '?')
    if (!castMember) return null;
    const chipStyle = getHierarchyStyle(castMember.hierarchy, theme);
    
    return (
      <Chip
        key={i}
        label={`#${castMember.hierarchy}`}
        size="small"
        variant="outlined"
        sx={{ 
          bgcolor: chipStyle.bg,
          borderColor: chipStyle.border,
          minWidth: '32px',
          '& .MuiChip-label': {
            fontWeight: 'bold',
            fontSize: '0.75rem',
            px: 1
          }
        }}
        title={`#${castMember.hierarchy}: ${castMember.name} as ${charName}`}
      />
    );
  });
});

// Schedule Component
const Schedule = ({ data, onUpdate, projectId, styles, locationsVersion }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [selectedSceneIndex, setSelectedSceneIndex] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingScene, setEditingScene] = useState(null);
  const [breaks, setBreaks] = useState([]);
  const [shootingDayScenes, setShootingDayScenes] = useState([]);
  const [loadingDayScenes, setLoadingDayScenes] = useState(false);
  const [nextDayScenes, setNextDayScenes] = useState([]);
  const [loadingNextDayScenes, setLoadingNextDayScenes] = useState(false);
  const [castMembers, setCastMembers] = useState([]);
  const [loadingCast, setLoadingCast] = useState(false);
  const [locations, setLocations] = useState([]);
  const theme = useTheme();

  // Load locations from dedicated locations API
  useEffect(() => {
    const fetchLocations = async () => {
      if (!projectId) return;
      
      try {
        console.log('🔄 Fetching locations from API for Schedule dropdown...');
        const { data: locationsData } = await axios.get(`/api/v1/locations/${projectId}`);
        console.log('✅ Loaded locations from API:', locationsData);
        
        // Format locations for dropdown
        const formattedLocations = locationsData.map(location => ({
          name: location.name,
          address: location.address || ''
        }));
        
        // Add some common fallback locations if no locations are available
        if (formattedLocations.length === 0) {
          formattedLocations.push(
            { name: 'Studio', address: '' },
            { name: 'Location TBD', address: '' },
            { name: 'Exterior', address: '' },
            { name: 'Interior', address: '' }
          );
        }
        
        setLocations(formattedLocations);
        console.log('📍 Schedule dropdown locations updated:', formattedLocations);
      } catch (error) {
        console.error('❌ Error fetching locations:', error);
        // Use fallback locations on error
        setLocations([
          { name: 'Studio', address: '' },
          { name: 'Location TBD', address: '' },
          { name: 'Exterior', address: '' },
          { name: 'Interior', address: '' }
        ]);
      }
    };

    fetchLocations();
  }, [projectId, locationsVersion]); // Re-fetch when projectId or locationsVersion changes

  // Table header styles
  const darkHeaderStyle = {
    fontWeight: 600,
    fontSize: '0.875rem',
    backgroundColor: alpha(theme.palette.common.black, 0.2),
    color: theme.palette.primary.light,
    borderBottom: `2px solid ${alpha(theme.palette.primary.main, 0.2)}`,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    whiteSpace: 'nowrap'
  };

  const lightHeaderStyle = {
    fontWeight: 600,
    fontSize: '0.875rem',
    backgroundColor: alpha(theme.palette.primary.main, 0.05),
    color: theme.palette.primary.main,
    borderBottom: `2px solid ${alpha(theme.palette.primary.main, 0.1)}`,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    whiteSpace: 'nowrap'
  };

  // Initialize schedule from data or use an empty array
  const [schedule, setSchedule] = useState(Array.isArray(data) ? data : Array.isArray(data?.schedule) ? data.schedule : []);
  
  // Debug output to check what data we're receiving
  useEffect(() => {
    console.log('Schedule component received data:', data);
    console.log('Current schedule state:', schedule);
  }, [data, schedule]);

  // Helper to shallow-compare scene arrays by stable keys
  const areScenesEqual = (a = [], b = []) => {
    if (a === b) return true;
    if (!Array.isArray(a) || !Array.isArray(b)) return false;
    if (a.length !== b.length) return false;
    // compare by a few stable properties including location for persistence
    const pick = (s) => ({
      id: s.id || s._id || s.sceneId || s.number || s.sceneNumber || s.index || null,
      order: s.order || s.scriptOrder || s.index || 0,
      title: s.title || s.slug || '',
      location: s.location || '',
      locationAddress: s.locationAddress || '',
    });
    for (let i = 0; i < a.length; i++) {
      const aa = pick(a[i]);
      const bb = pick(b[i]);
      if (aa.id !== bb.id || aa.order !== bb.order || aa.title !== bb.title || 
          aa.location !== bb.location || aa.locationAddress !== bb.locationAddress) return false;
    }
    return true;
  };

  // Update local schedule when data changes (only depend on data)
  useEffect(() => {
    if (!data) return;

    if (Array.isArray(data)) {
      console.log('Data is an array, considering schedule update');
      if (!areScenesEqual(schedule, data)) {
        setSchedule(data);
      }
      return;
    }

    if (Array.isArray(data.schedule)) {
      console.log('Data contains schedule array, considering update from data.schedule');
      if (!areScenesEqual(schedule, data.schedule)) {
        setSchedule(data.schedule);
      }
    }
    if (Array.isArray(data.nextDaySchedule)) {
      console.log('Data contains nextDaySchedule array, considering update from data.nextDaySchedule');
      if (!areScenesEqual(nextDayScenes, data.nextDaySchedule)) {
        setNextDayScenes(data.nextDaySchedule);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);
  
  // Get current shooting day from callsheet data
  const currentShootingDay = data?.shootingDay || 1;
  
  // Add this for debugging
  useEffect(() => {
    console.log('Current shooting day detected:', currentShootingDay);
    console.log('Full callsheet data:', data);
  }, [currentShootingDay, data]);

  useEffect(() => {
    if (projectId) {
      fetchBreaks();
      fetchCastMembers();
    }
  }, [projectId]);

  // Listen for scenesReordered and forceScheduleRefresh events from OneLiner to refresh schedule
  useEffect(() => {
    const handleScenesReordered = (event) => {
      if (event.detail?.projectId === projectId) {
        console.log('📋 Scenes reordered in OneLiner, refreshing schedule...');
        // Clear localStorage cache and refetch scenes
        localStorage.removeItem(`schedule_${projectId}_day_${currentShootingDay}`);
        fetchScenesForShootingDay(currentShootingDay);
        // Also refresh next day scenes
        const nextDay = currentShootingDay + 1;
        fetchScenesForNextDay(nextDay);
      }
    };

    const handleForceRefresh = (event) => {
      if (event.detail?.projectId === projectId) {
        console.log('📋 Force schedule refresh triggered, refetching scenes...');
        // Clear localStorage cache for all days
        for (let i = 1; i <= 30; i++) {
          localStorage.removeItem(`schedule_${projectId}_day_${i}`);
        }
        // Refetch scenes for current day
        const day = event.detail?.shootingDay || currentShootingDay;
        fetchScenesForShootingDay(day);
        // Also refresh next day scenes
        fetchScenesForNextDay(day + 1);
      }
    };

    window.addEventListener('scenesReordered', handleScenesReordered);
    window.addEventListener('forceScheduleRefresh', handleForceRefresh);
    return () => {
      window.removeEventListener('scenesReordered', handleScenesReordered);
      window.removeEventListener('forceScheduleRefresh', handleForceRefresh);
    };
  }, [projectId, currentShootingDay]);
  
  // Fetch scenes for the current shooting day whenever the shooting day changes
  // Track if we've already updated the parent to prevent loops
  const [hasUpdatedParent, setHasUpdatedParent] = useState(false);
  
  // Effect to fetch scenes when shooting day changes
  useEffect(() => {
    if (currentShootingDay && projectId) {
      console.log(`Shooting day changed to: ${currentShootingDay} fetching scenes...`);
      console.log('Loading scenes for current shooting day:', currentShootingDay);
      
      // Reset the update flag when shooting day changes
      setHasUpdatedParent(false);
      
      // First check if we have scenes in localStorage
      try {
        // Clear localStorage for this day to ensure we always get fresh scenes
        // This ensures we don't get stuck with incorrect scenes
        localStorage.removeItem(`schedule_${projectId}_day_${currentShootingDay}`);
        console.log(`Cleared localStorage for day ${currentShootingDay} to ensure fresh scenes`);
        
        /* Disabled localStorage loading to ensure we always get fresh scenes
        const savedScenes = localStorage.getItem(`schedule_${projectId}_day_${currentShootingDay}`);
        if (savedScenes) {
          const parsedScenes = JSON.parse(savedScenes);
          if (Array.isArray(parsedScenes) && parsedScenes.length > 0) {
            console.log(`Found ${parsedScenes.length} saved scenes in localStorage for day ${currentShootingDay}`);
            setSchedule(parsedScenes);
            setShootingDayScenes(parsedScenes);
            setSuccess(`Loaded ${parsedScenes.length} scenes from local storage for Day ${currentShootingDay}`);
            return; // Don't fetch from server if we have scenes locally
          }
        }
        */
      } catch (storageError) {
        console.warn('Error checking localStorage for scenes:', storageError);
      }
      
      // Always fetch fresh scenes when shooting day changes
      // The data.schedule from parent may contain previous day's scenes
      console.log('Fetching fresh scenes from server for day', currentShootingDay);
      fetchScenesForShootingDay(currentShootingDay);
      fetchScenesForNextDay(currentShootingDay + 1);
    }
  }, [currentShootingDay, projectId]); // Only depend on these two values
  
  // We now directly call populateScheduleFromShootingDay in the fetchScenesForShootingDay function
  // to ensure immediate updating without waiting for the state to update
  /*
  useEffect(() => {
    if (shootingDayScenes.length > 0) {
      console.log('Auto-populating schedule with scenes for day:', currentShootingDay);
      console.log('Scenes to populate:', shootingDayScenes);
      populateScheduleFromShootingDay();
    } else {
      console.log('No scenes to populate for day:', currentShootingDay);
    }
  }, [shootingDayScenes, currentShootingDay]);
  */
  
  // We'll use a ref to track the last update to prevent loops
  const lastUpdateRef = React.useRef({ schedule: [], nextDaySchedule: [] });
  
  // This effect runs once when the component mounts to reset the update ref
  useEffect(() => {
    lastUpdateRef.current = { schedule: [], nextDaySchedule: [] };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  
  // This effect ensures we have the latest data for both schedules
  // but only updates when there's an actual change to prevent loops
  useEffect(() => {
    if (typeof onUpdate !== 'function') return;
    
    // Check if we have data to update
    const hasMainSchedule = schedule.length > 0;
    const hasNextDaySchedule = nextDayScenes.length > 0;
    
    // Check if the data has actually changed to prevent loops
    const mainScheduleChanged = hasMainSchedule &&
      (!areScenesEqual(lastUpdateRef.current.schedule, schedule));
    const nextDayScheduleChanged = hasNextDaySchedule &&
      (!areScenesEqual(lastUpdateRef.current.nextDaySchedule, nextDayScenes));
    
    // Only update if something has actually changed
    if (mainScheduleChanged || nextDayScheduleChanged) {
      console.log(`Schedule update needed - Main: ${mainScheduleChanged ? 'Changed' : 'Unchanged'}, Next day: ${nextDayScheduleChanged ? 'Changed' : 'Unchanged'}`);
      
      // Update the parent component with the latest data
      onUpdate({
        schedule: schedule,
        nextDaySchedule: nextDayScenes
      });
      
      // Update our ref to track what we've sent
      lastUpdateRef.current = {
        schedule: [...schedule],
        nextDaySchedule: [...nextDayScenes]
      };
      
      console.log('Updated parent with latest schedule data');
    }
  }, [schedule, nextDayScenes, onUpdate]);

  // Removed fetchScripts function as we're now using the shooting day import

  // Removed fetchScenes function as we're now using the shooting day import
  
  // Define fetchScenesForNextDay to get the next day's scenes
  const fetchScenesForNextDay = async (nextDay) => {
    if (!projectId) {
      console.error('No projectId provided');
      return;
    }
    
    console.log(`Attempting to fetch scenes for next day ${nextDay} of project ${projectId}`);
    setLoadingNextDayScenes(true);
    
    try {
      // Get authentication token from localStorage
      const token = localStorage.getItem('token');
      if (!token) {
        console.error('Authentication token not found');
        setLoadingNextDayScenes(false);
        return;
      }
      
      // USE BOTH AUTHENTICATION HEADERS for consistency with your auth system
      const headers = {
        'Authorization': `Bearer ${token}`,
        'x-auth-token': token
      };
      
      // Fetch scenes for the project
      console.log(`Fetching scenes for project ${projectId}...`);
      const scenesResponse = await axios.get(
        `/api/v1/scripts/project/${projectId}/scenes`,
        { headers }
      );
      
      // Handle different possible scene response formats
      let allScenes = [];
      
      if (scenesResponse?.data?.scenes && Array.isArray(scenesResponse.data.scenes)) {
        allScenes = scenesResponse.data.scenes;
      } else if (Array.isArray(scenesResponse?.data)) {
        allScenes = scenesResponse.data;
      } else {
        console.error('Invalid scenes response format:', scenesResponse.data);
        console.log('Attempting to extract scenes from response...');
        
        // Try to extract scenes from any property that might contain them
        const responseData = scenesResponse.data || {};
        const possibleSceneArrays = Object.values(responseData)
          .filter(value => Array.isArray(value) && value.length > 0);
        
        if (possibleSceneArrays.length > 0) {
          // Use the largest array as it's most likely to be the scenes
          allScenes = possibleSceneArrays.reduce((largest, current) => 
            current.length > largest.length ? current : largest, []);
          console.log(`Extracted ${allScenes.length} scenes from response`);
        } else {
          setNextDayScenes([]);
          setLoadingNextDayScenes(false);
          return;
        }
      }
      
      // Fetch breaks using BOTH auth headers
      console.log(`Fetching breaks for project ${projectId}...`);
      const breaksResponse = await axios.get(
        `/api/v1/scripts/${projectId}/breaks`,
        { headers }
      );
      
      // Handle both array and object response formats
      let allBreaks = [];
      if (Array.isArray(breaksResponse.data)) {
        allBreaks = breaksResponse.data;
      } else if (breaksResponse.data.breaks && Array.isArray(breaksResponse.data.breaks)) {
        allBreaks = breaksResponse.data.breaks;
      } else {
        console.error('Could not find breaks array in response:', breaksResponse.data);
        setNextDayScenes([]);
        setLoadingNextDayScenes(false);
        return;
      }
      
      // Sort scenes by order
      const sortedScenes = [...allScenes].sort((a, b) => {
        const orderA = a.order || a.scriptOrder || a.index || 0;
        const orderB = b.order || b.scriptOrder || b.index || 0;
        return orderA - orderB;
      });
      
      // Filter day breaks and sort them by scene index
      const allDayBreaks = allBreaks
        .filter(brk => brk.type === 'day' || brk.type === 'DAY_BREAK' || brk.type === 'DAY' || brk.breakType === 'day');
      
      const dayBreaks = allDayBreaks.sort((a, b) => {
        const indexA = a.sceneIndex || a.afterSceneIndex || a.index || 0;
        const indexB = b.sceneIndex || b.afterSceneIndex || b.index || 0;
        return indexA - indexB;
      });
      
      // Determine which scenes belong to the next shooting day
      let nextDayScenesList = [];
      
      if (dayBreaks.length === 0) {
        // If there are no day breaks, all scenes are part of day 1
        if (nextDay === 1) {
          nextDayScenesList = sortedScenes;
        }
      } else if (nextDay === 1) {
        // For day 1, get scenes before the first day break
        const firstDayBreakIndex = dayBreaks.length > 0 ? 
          (dayBreaks[0].sceneIndex || dayBreaks[0].afterSceneIndex || dayBreaks[0].index || 0) : 
          sortedScenes.length;
          
        nextDayScenesList = sortedScenes.filter((_, index) => index <= firstDayBreakIndex);
      } else {
        // For days > 1, find the scenes between the appropriate day breaks
        const prevDayIndex = nextDay - 1;
        const currDayIndex = nextDay;
        
        // Find the break that marks the end of the previous day
        const prevDayBreak = dayBreaks.find(brk => 
          brk.day === prevDayIndex || 
          brk.dayNumber === prevDayIndex || 
          brk.customDayNumber === prevDayIndex
        ) || (prevDayIndex < dayBreaks.length ? dayBreaks[prevDayIndex - 1] : null);
        
        // Find the break that marks the end of the current day
        const currDayBreak = dayBreaks.find(brk => 
          brk.day === currDayIndex || 
          brk.dayNumber === currDayIndex || 
          brk.customDayNumber === currDayIndex
        ) || (currDayIndex < dayBreaks.length ? dayBreaks[currDayIndex - 1] : null);
        
        if (prevDayBreak) {
          const prevDayBreakIndex = prevDayBreak.sceneIndex || prevDayBreak.afterSceneIndex || prevDayBreak.index || 0;
          
          if (currDayBreak) {
            // If we have both breaks, get scenes between them
            const currDayBreakIndex = currDayBreak.sceneIndex || currDayBreak.afterSceneIndex || currDayBreak.index || 0;
            nextDayScenesList = sortedScenes.filter((_, index) => 
              index > prevDayBreakIndex && index <= currDayBreakIndex
            );
          } else {
            // If we only have the previous day break, get all scenes after it
            nextDayScenesList = sortedScenes.filter((_, index) => index > prevDayBreakIndex);
          }
        }
      }
      
      console.log(`Found ${nextDayScenesList.length} scenes for next day ${nextDay}`);
      
      // Format scenes for display
      const formattedNextDayScenes = nextDayScenesList.map(scene => formatSceneForSchedule(scene));
      console.log('Formatted next day scenes:', formattedNextDayScenes);
      
      // Set the next day scenes in state - this will trigger the synchronization effect
      // which will handle updating the parent component
      setNextDayScenes(formattedNextDayScenes);
      
      setLoadingNextDayScenes(false);
      
    } catch (error) {
      console.error(`Error fetching scenes for next day ${nextDay}:`, error);
      setNextDayScenes([]);
      setLoadingNextDayScenes(false);
    }
  };

  // Define fetchScenesForShootingDay early to avoid dependency issues
  const fetchScenesForShootingDay = async (day) => {
    if (!projectId) {
      console.error('No projectId provided');
      return;
    }
    
    console.log(`Attempting to fetch scenes for day ${day} of project ${projectId}`);
    setLoadingDayScenes(true);
    setError(null);
    
    try {
      // Get authentication token from localStorage
      const token = localStorage.getItem('token');
      if (!token) {
        console.error('Authentication token not found');
        setError('Authentication token not found');
        setLoadingDayScenes(false);
        return;
      }
      
      // USE BOTH AUTHENTICATION HEADERS for consistency with your auth system
      const headers = {
        'Authorization': `Bearer ${token}`,
        'x-auth-token': token
      };
      
      // Fetch scenes for the project
      console.log(`Fetching scenes for project ${projectId}...`);
      const scenesResponse = await axios.get(
        `/api/v1/scripts/project/${projectId}/scenes`,
        { headers }
      );
      
      // Handle different possible scene response formats
      let allScenes = [];
      
      if (scenesResponse?.data?.scenes && Array.isArray(scenesResponse.data.scenes)) {
        allScenes = scenesResponse.data.scenes;
      } else if (Array.isArray(scenesResponse?.data)) {
        allScenes = scenesResponse.data;
      } else {
        console.error('Invalid scenes response format:', scenesResponse.data);
        console.log('Attempting to extract scenes from response...');
        
        // Try to extract scenes from any property that might contain them
        const responseData = scenesResponse.data || {};
        const possibleSceneArrays = Object.values(responseData)
          .filter(value => Array.isArray(value) && value.length > 0);
        
        if (possibleSceneArrays.length > 0) {
          // Use the largest array as it's most likely to be the scenes
          allScenes = possibleSceneArrays.reduce((largest, current) => 
            current.length > largest.length ? current : largest, []);
          console.log(`Extracted ${allScenes.length} scenes from response`);
        } else {
          setShootingDayScenes([]);
          setLoadingDayScenes(false);
          setError('No scenes found or invalid format');
          return;
        }
      }
      
      console.log(`Found ${allScenes.length} scenes for project`);
      // Log the first few scenes to help with debugging
      console.log('Sample scenes:', allScenes.slice(0, 3));
      
      // Fetch breaks using BOTH auth headers
      console.log(`Fetching breaks for project ${projectId}...`);
      const breaksResponse = await axios.get(
        `/api/v1/scripts/${projectId}/breaks`,
        { headers }
      );
      
      // Check for various possible break response formats we might encounter
      if (!breaksResponse?.data) {
        console.error('Invalid breaks response format:', breaksResponse.data);
        setError('No breaks found or invalid format');
        
        // If no breaks, but we have scenes and day is 1, use all scenes
        if (day === 1 && allScenes.length > 0) {
          console.log('No breaks found but day is 1, using all scenes');
          setShootingDayScenes(allScenes);
        } else {
          setShootingDayScenes([]);
        }
        setLoadingDayScenes(false);
        return;
      }
      
      // Handle both array and object response formats
      let allBreaks = [];
      if (Array.isArray(breaksResponse.data)) {
        allBreaks = breaksResponse.data;
      } else if (breaksResponse.data.breaks && Array.isArray(breaksResponse.data.breaks)) {
        allBreaks = breaksResponse.data.breaks;
      } else {
        console.error('Could not find breaks array in response:', breaksResponse.data);
        // If no breaks array found but we have scenes and day is 1, use all scenes
        if (day === 1 && allScenes.length > 0) {
          console.log('No breaks array found but day is 1, using all scenes');
          setShootingDayScenes(allScenes);
        } else {
          setShootingDayScenes([]);
        }
        setLoadingDayScenes(false);
        return;
      }
      
      console.log(`Found ${allBreaks.length} breaks`);
      
      // Sort scenes by order
      const sortedScenes = [...allScenes].sort((a, b) => {
        const orderA = a.order || a.scriptOrder || a.index || 0;
        const orderB = b.order || b.scriptOrder || b.index || 0;
        return orderA - orderB;
      });
      
      // Filter day breaks and sort them by scene index
      // Make sure we're checking all possible day break types used in the system
      const allDayBreaks = allBreaks
        .filter(brk => brk.type === 'day' || brk.type === 'DAY_BREAK' || brk.type === 'DAY' || brk.breakType === 'day');
      
      console.log('All day breaks before sorting:', allDayBreaks.map(brk => ({
        id: brk.id,
        type: brk.type,
        breakType: brk.breakType,
        sceneIndex: brk.sceneIndex,
        afterSceneIndex: brk.afterSceneIndex,
        index: brk.index,
        title: brk.title
      })));
      
      const dayBreaks = allDayBreaks.sort((a, b) => {
        // Handle different property naming conventions
        const indexA = a.sceneIndex || a.afterSceneIndex || a.index || 0;
        const indexB = b.sceneIndex || b.afterSceneIndex || b.index || 0;
        return indexA - indexB;
      });

      console.log(`Found ${dayBreaks.length} day breaks`);
      console.log('Day breaks:', dayBreaks);

      // Determine which scenes belong to the requested shooting day
      let dayScenes = [];
      
      console.log('Determining scenes for shooting day:', day);
      console.log('Total scenes:', sortedScenes.length);
      console.log('Day breaks:', dayBreaks);
      
      // Examine the first few scenes to understand their structure
      console.log('First 5 scenes structure:', sortedScenes.slice(0, 5).map(scene => ({
        id: scene.id,
        order: scene.order,
        scriptOrder: scene.scriptOrder,
        index: scene.index,
        sceneNumber: scene.number || scene.sceneNumber,
        description: scene.description ? scene.description.substring(0, 30) + '...' : 'No description'
      })));
      
      if (dayBreaks.length === 0) {
        // If there are no day breaks, all scenes are part of day 1
        if (day === 1) {
          console.log('No day breaks found. All scenes are part of day 1');
          dayScenes = sortedScenes;
        } else {
          console.log(`No day breaks found, but requested day ${day} (not 1). No scenes.`);
        }
      } else if (day === 1) {
        // SUPER COMPREHENSIVE APPROACH FOR DAY 1
        console.log('Using super comprehensive approach for day 1 scenes');
        
        // First, check if any scenes have explicit day 1 markings
        const explicitDay1Scenes = sortedScenes.filter(scene => {
          return scene.day === 1 || 
                 scene.shootingDay === 1 || 
                 scene.shooting_day === 1 ||
                 (scene.metadata && (scene.metadata.day === 1 || scene.metadata.shootingDay === 1));
        });
        
        console.log(`Found ${explicitDay1Scenes.length} scenes explicitly marked as day 1`);
        
        // If we have day breaks, find the end of day 1 break and include all scenes above it
        if (dayBreaks.length > 0) {
          // Find the day break that marks the end of day 1
          const endOfDay1Break = dayBreaks.find(breakItem => {
            // Check if this break marks the end of day 1
            return (
              (breakItem.day === 1 || breakItem.dayNumber === 1) ||
              (breakItem.customDayNumber === 1) ||
              (breakItem.metadata && (breakItem.metadata.day === 1 || breakItem.metadata.dayNumber === 1))
            );
          }) || dayBreaks[0]; // Fallback to first break if no explicit day 1 break found
          
          console.log('End of Day 1 break:', endOfDay1Break);
          
          // Get the scene index of the end of day 1 break
          const endOfDay1Index = endOfDay1Break.sceneIndex || endOfDay1Break.afterSceneIndex || endOfDay1Break.index || 0;
          
          console.log(`End of Day 1 break is at scene index ${endOfDay1Index}`);
          
          // Get all scenes BEFORE OR AT this index (inclusive)
          const scenesBeforeEndOfDay1 = sortedScenes.filter(scene => {
            const sceneOrder = scene.order || scene.scriptOrder || scene.index || 0;
            return sceneOrder <= endOfDay1Index;
          });
          
          console.log(`Found ${scenesBeforeEndOfDay1.length} scenes before or at end of day 1 break (index ${endOfDay1Index})`);
          
          // Combine explicit day 1 scenes with scenes before end of day 1 break
          const combinedScenes = [...new Set([...explicitDay1Scenes, ...scenesBeforeEndOfDay1])];
          
          if (combinedScenes.length > 0) {
            dayScenes = combinedScenes;
            console.log(`Using combined approach: ${dayScenes.length} scenes for day 1`);
          } else {
            // If we still don't have scenes, take the first 25% of all scenes
            const fallbackCount = Math.max(Math.ceil(sortedScenes.length * 0.25), 1);
            dayScenes = sortedScenes.slice(0, fallbackCount);
            console.log(`Using fallback percentage: ${dayScenes.length} scenes for day 1 (${fallbackCount} scenes)`);
          }
        } else if (explicitDay1Scenes.length > 0) {
          // If no day breaks but we have explicit day 1 scenes, use those
          dayScenes = explicitDay1Scenes;
          console.log(`Using explicit day 1 scenes: ${dayScenes.length} scenes`);
        } else {
          // If no day breaks and no explicit day 1 scenes, use ALL scenes
          // This is the most drastic fallback but ensures we show something
          dayScenes = sortedScenes;
          console.log(`EMERGENCY FALLBACK: Using ALL ${sortedScenes.length} scenes for day 1`);
        }
        
        // Final check - if we still have no scenes, use ALL scenes
        if (dayScenes.length === 0 && sortedScenes.length > 0) {
          dayScenes = sortedScenes;
          console.log(`FINAL EMERGENCY FALLBACK: Using ALL ${sortedScenes.length} scenes for day 1`);
        }
      } else if (day <= dayBreaks.length) {
        // Days 2 to N-1: Scenes BETWEEN day breaks
        // This must match exactly how the OneLiner component defines days
        console.log(`Calculating scenes for Day ${day}`);
        
        // For day 2, we need scenes AFTER the end of day 1 break and BEFORE the end of day 2 break
        
        // Find the break that marks the end of the previous day
        const endOfPrevDayBreak = dayBreaks.find(breakItem => {
          return (
            (breakItem.day === day-1 || breakItem.dayNumber === day-1) ||
            (breakItem.customDayNumber === day-1) ||
            (breakItem.metadata && (breakItem.metadata.day === day-1 || breakItem.metadata.dayNumber === day-1))
          );
        }) || dayBreaks[day-2]; // Fallback to appropriate break by position
        
        // Find the break that marks the end of the current day
        const endOfCurrentDayBreak = dayBreaks.find(breakItem => {
          return (
            (breakItem.day === day || breakItem.dayNumber === day) ||
            (breakItem.customDayNumber === day) ||
            (breakItem.metadata && (breakItem.metadata.day === day || breakItem.metadata.dayNumber === day))
          );
        }) || dayBreaks[day-1]; // Fallback to appropriate break by position
        
        console.log(`End of Day ${day-1} break:`, endOfPrevDayBreak);
        console.log(`End of Day ${day} break:`, endOfCurrentDayBreak);
        
        // Get the scene indices for the breaks, trying all possible property names
        const startBreakIndex = endOfPrevDayBreak.sceneIndex || endOfPrevDayBreak.afterSceneIndex || endOfPrevDayBreak.index || 0;
        const endBreakIndex = endOfCurrentDayBreak.sceneIndex || endOfCurrentDayBreak.afterSceneIndex || endOfCurrentDayBreak.index || 0;
        
        console.log(`Day ${day}: Getting scenes between scene indices ${startBreakIndex} and ${endBreakIndex}`);
        
        // Get all scenes AFTER the previous day break (exclusive) and BEFORE OR AT the current day break (inclusive)
        dayScenes = sortedScenes.filter(scene => {
          const sceneOrder = scene.order || scene.scriptOrder || scene.index || 0;
          const result = sceneOrder > startBreakIndex && sceneOrder <= endBreakIndex;
          if (result) {
            console.log(`Scene ${scene.id || ''} with order ${sceneOrder} is in day ${day} (between ${startBreakIndex} and ${endBreakIndex})`);
          }
          return result;
        });
        
        // Also include scenes explicitly marked as this day
        const explicitDayScenes = sortedScenes.filter(scene => {
          return scene.day === day || 
                 scene.shootingDay === day || 
                 scene.shooting_day === day ||
                 (scene.metadata && (scene.metadata.day === day || scene.metadata.shootingDay === day));
        });
        
        console.log(`Found ${explicitDayScenes.length} scenes explicitly marked as day ${day}`);
        
        // Combine both sets of scenes
        const combinedScenes = [...new Set([...dayScenes, ...explicitDayScenes])];
        
        if (combinedScenes.length > 0) {
          dayScenes = combinedScenes;
          console.log(`Combined approach: Found ${dayScenes.length} scenes for day ${day}`);
        } else {
          console.log(`No scenes found for day ${day} using standard approach, using fallback`);
          // FALLBACK: Take scenes between the appropriate indices in the sorted list
          const totalScenes = sortedScenes.length;
          const dayCount = dayBreaks.length + 1;
          const scenesPerDay = Math.ceil(totalScenes / dayCount);
          const startIndex = (day - 1) * scenesPerDay;
          const endIndex = Math.min(day * scenesPerDay, totalScenes);
          
          dayScenes = sortedScenes.slice(startIndex, endIndex);
          console.log(`Using fallback approach, taking scenes ${startIndex}-${endIndex} for day ${day}`);
        }
      } else if (day === dayBreaks.length + 1) {
        // Last day: Scenes AFTER the last day break
        // This must match exactly how the OneLiner component defines the last day
        console.log(`Calculating scenes for Last Day (${day})`);
        
        // Find the break that marks the end of the previous day (second to last day)
        const endOfPrevDayBreak = dayBreaks.find(breakItem => {
          return (
            (breakItem.day === day-1 || breakItem.dayNumber === day-1) ||
            (breakItem.customDayNumber === day-1) ||
            (breakItem.metadata && (breakItem.metadata.day === day-1 || breakItem.metadata.dayNumber === day-1))
          );
        }) || dayBreaks[dayBreaks.length - 1]; // Fallback to last break
        
        console.log(`End of Day ${day-1} break (before last day):`, endOfPrevDayBreak);
        
        // Get the scene index of the last break
        const lastBreakIndex = endOfPrevDayBreak.sceneIndex || endOfPrevDayBreak.afterSceneIndex || endOfPrevDayBreak.index || 0;
        
        console.log(`Last day (${day}): Getting scenes after scene index ${lastBreakIndex}`);
        
        // Get all scenes AFTER the last break index (exclusive)
        dayScenes = sortedScenes.filter(scene => {
          const sceneOrder = scene.order || scene.scriptOrder || scene.index || 0;
          const result = sceneOrder > lastBreakIndex;
          if (result) {
            console.log(`Scene ${scene.id || ''} with order ${sceneOrder} is in last day ${day} (after ${lastBreakIndex})`);
          }
          return result;
        });
        
        // Also include scenes explicitly marked as the last day
        const explicitLastDayScenes = sortedScenes.filter(scene => {
          return scene.day === day || 
                 scene.shootingDay === day || 
                 scene.shooting_day === day ||
                 (scene.metadata && (scene.metadata.day === day || scene.metadata.shootingDay === day));
        });
        
        console.log(`Found ${explicitLastDayScenes.length} scenes explicitly marked as last day ${day}`);
        
        // Combine both sets of scenes
        const combinedScenes = [...new Set([...dayScenes, ...explicitLastDayScenes])];
        
        if (combinedScenes.length > 0) {
          dayScenes = combinedScenes;
          console.log(`Combined approach: Found ${dayScenes.length} scenes for last day ${day}`);
        } else {
          console.log(`No scenes found for last day ${day} using standard approach, using fallback`);
          // FALLBACK: Take the last quarter of scenes
          const totalScenes = sortedScenes.length;
          const startIndex = Math.floor(totalScenes * 0.75);
          
          dayScenes = sortedScenes.slice(startIndex);
          console.log(`Using fallback approach, taking scenes after index ${startIndex} for last day ${day}`);
        }
      } else {
        console.log(`Requested day ${day} exceeds available shooting days (${dayBreaks.length + 1})`);
      }

      console.log(`Found ${dayScenes.length} scenes for day ${day}:`, dayScenes);
      
      // Set the scenes for this shooting day
      setShootingDayScenes(dayScenes);
      
      // Force a re-render by setting a timeout to populate schedule
      if (dayScenes.length > 0) {
        console.log('Will auto-populate schedule with scenes shortly...');
        // Call populateScheduleFromShootingDay directly
        populateScheduleFromShootingDay(dayScenes);
      } else {
        // Emergency: If we still have no scenes but there are scenes in the script, grab the first ones
        if (sortedScenes.length > 0) {
          console.log('EMERGENCY FALLBACK: No scenes found but script has scenes. Taking first 10 scenes.');
          const emergencyScenes = sortedScenes.slice(0, 10);
          setShootingDayScenes(emergencyScenes);
          populateScheduleFromShootingDay(emergencyScenes);
        }
      }
      
    } catch (err) {
      console.error('Error fetching scenes for shooting day:', err);
      setError(`Error fetching scenes: ${err.message}`);
      setShootingDayScenes([]);
    } finally {
      setLoadingDayScenes(false);
    }
  };

  // Fetch cast members for the project
  const fetchCastMembers = async () => {
    if (!projectId) return;
    
    setLoadingCast(true);
    try {
      // Get authentication token from localStorage
      const token = localStorage.getItem('token');
      if (!token) {
        console.error('No authentication token found for cast loading');
        return;
      }

      const response = await axios.get(`/api/v1/cast/project/${projectId}`, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          'x-auth-token': token
        }
      });
      
      console.log('Cast members loaded for schedule:', response.data);
      setCastMembers(response.data);
    } catch (err) {
      console.error('Error loading cast members for schedule:', err);
      // Don't show an error to the user as this is an enhancement
    } finally {
      setLoadingCast(false);
    }
  };

  // Function to get color based on hierarchy number
  const getHierarchyColor = (hierarchy) => {
    switch(Number(hierarchy)) {
      case 1: return { bg: '#FFF8DC', border: '#FFD700' }; // Lead - Gold
      case 2: return { bg: '#F5F5F5', border: '#C0C0C0' }; // Supporting - Silver
      case 3: return { bg: '#FFF0E6', border: '#CD7F32' }; // Featured - Bronze
      case 4: return { bg: '#F0F8FF', border: '#4682B4' }; // Minor Supporting - Steel Blue
      case 5: return { bg: '#F5F5F5', border: '#708090' }; // Minor - Slate Gray
      case 6: return { bg: '#F5F5F5', border: '#A9A9A9' }; // Extra - Dark Gray
      case 7: return { bg: '#F5F5F5', border: '#D3D3D3' }; // Background - Light Gray
      default: return { bg: '#F5F5F5', border: '#708090' }; // Default - Slate Gray
    }
  };

  // Fetch breaks from the OneLiner component
  const fetchBreaks = async () => {
    if (!projectId) return;
    
    try {
      // Get authentication token from localStorage
      const token = localStorage.getItem('token');
      if (!token) {
        console.error('No authentication token found');
        return;
      }
      
      const response = await axios.get(
        `/api/v1/scripts/${projectId}/breaks`,
        { 
          headers: { 
            'Authorization': `Bearer ${token}`,
            'x-auth-token': token
          }
        }
      );
      
      if (response && response.data) {
        setBreaks(response.data);
      }
    } catch (err) {
      console.error('Error fetching breaks:', err);
    }
  };
  
  /**
   * Formats a page count in industry-standard 1/8 page increments
   * In film production, each page is divided into 8 equal parts (approximately 1 inch each)
   * This is the standard used on breakdown sheets, DOOD reports, and shooting schedules
   * @param {number|string} pages - The page count to format
   * @returns {string} - The formatted page count (e.g., "2 4/8" or "2½")
   */
  const formatPagesInEighths = (pages) => {
    if (!pages && pages !== 0) return '';
    
    // Convert to number if it's a string
    const numPages = typeof pages === 'string' ? parseFloat(pages) : pages;
    
    if (isNaN(numPages)) return '';
    
    // Get whole pages and fractional part
    const wholePages = Math.floor(numPages);
    const fraction = numPages - wholePages;
    
    // If it's a whole number, just return it as a string
    if (fraction === 0) return wholePages.toString();
    
    // Convert the fraction to eighths
    const eighths = Math.round(fraction * 8);
    
    // Handle special cases for common fractions
    if (eighths === 4) return `${wholePages}½`;
    if (eighths === 8) return `${wholePages + 1}`;
    
    // Return the formatted string with the fraction in eighths
    return `${wholePages} ${eighths}/8`;
  };
  
  // Populate schedule from the current shooting day scenes
  const populateScheduleFromShootingDay = (scenes = null) => {
    // Use provided scenes or fall back to the state
    const scenesToProcess = scenes || shootingDayScenes;
    
    console.log('populateScheduleFromShootingDay called with:', {
      scenesPassedDirectly: scenes ? scenes.length : 'none',
      shootingDayScenes: shootingDayScenes ? shootingDayScenes.length : 'none',
      scenesToProcess: scenesToProcess ? scenesToProcess.length : 'none'
    });
    
    if (!scenesToProcess || scenesToProcess.length === 0) {
      console.log('No scenes to populate schedule with');
      setError('No scenes available for the current shooting day');
      return;
    }
    
    // Clear any previous error messages since we now have scenes
    setError(null);
    
    // Log the first few scenes to help with debugging
    console.log('Sample scenes to process:', scenesToProcess.slice(0, 3));
    
    console.log(`Populating schedule with ${scenesToProcess.length} scenes`);
    console.log('Auto-populating schedule with scenes for day:', currentShootingDay);
    console.log('Raw scenes to populate:', scenesToProcess);
    
    try {
      // Create a new array for our properly formatted scenes
      const formattedScenes = [];
      
      // Process each scene into our standard format
      scenesToProcess.forEach((scene, index) => {
        console.log(`Processing scene ${index}:`, scene);
        
        // Skip invalid scene objects
        if (!scene) {
          console.log(`Scene ${index} is null or undefined, skipping`);
          return;
        }
        
        try {
          const formattedScene = formatSceneForSchedule(scene);
          console.log(`Formatted scene ${index}:`, formattedScene);
          formattedScenes.push(formattedScene);
        } catch (formatError) {
          console.error(`Error formatting scene ${index}:`, formatError);
          // Try to create a basic formatted scene to prevent complete failure
          const basicFormattedScene = {
            setScene: 'Unknown',
            sceneNumber: scene.id || `${index+1}`,
            description: scene.description || 'No description available',
            cast: '',
            dayNight: 'D',
            pages: '',
            location: scene.location || '',
            location_type: scene.location_type || 'Unknown location'
          };
          console.log(`Created basic formatted scene for ${index}:`, basicFormattedScene);
          formattedScenes.push(basicFormattedScene);
        }
      });
      
      console.log(`Formatted ${formattedScenes.length} scenes for schedule`);
      
      if (formattedScenes.length === 0) {
        console.log('No scenes were successfully formatted');
        setError('No valid scenes could be processed for the schedule');
        return;
      }
      
      // === Important: Update local state first ===
      setSchedule(formattedScenes);
      
      // Clear any error messages since we now have scenes
      setError(null);
      
      // Set the schedule in state - this will trigger the synchronization effect
      // which will handle updating the parent component
      setSchedule(formattedScenes);
      
      // Persist the schedule in localStorage as a backup
      try {
        localStorage.setItem(`schedule_${projectId}_day_${currentShootingDay}`, JSON.stringify(formattedScenes));
        console.log('Saved scenes to localStorage as backup');
      } catch (storageError) {
        console.warn('Could not save scenes to localStorage:', storageError);
      }
      

    } catch (err) {
      console.error('Error formatting scenes:', err);
      setError('Error formatting scenes: ' + err.message);
    }
  };
  
  // Format a scene object for display in the schedule
  const formatSceneForSchedule = (scene) => {
    try {
      console.log('Formatting scene for schedule:', scene);
      
      // If scene is null or undefined, return a fallback scene
      if (!scene) {
        console.log('Scene is null or undefined, returning fallback');
        return {
          setScene: 'Unknown',
          sceneNumber: 'N/A',
          description: 'Unknown scene',
          cast: '',
          dayNight: 'DAY',
          pages: '',
          location: 'Unknown'
        };
      }
      
      // Extract scene number, handling different possible formats
      let sceneNumber = '';
      if (scene.sceneNumber) {
        if (typeof scene.sceneNumber === 'object' && scene.sceneNumber.scriptOrder) {
          sceneNumber = scene.sceneNumber.scriptOrder;
        } else if (typeof scene.sceneNumber === 'object' && scene.sceneNumber.standard) {
          sceneNumber = scene.sceneNumber.standard.toString();
        } else {
          sceneNumber = scene.sceneNumber.toString();
        }
      } else if (scene.scene_number) {
        sceneNumber = scene.scene_number;
      } else if (scene.number) {
        sceneNumber = scene.number;
      } else if (scene.id && typeof scene.id === 'string' && scene.id.includes('-')) {
        // Try to extract scene number from ID (e.g., "scene-12A" -> "12A")
        const match = scene.id.match(/scene-([\w\d]+)/i);
        if (match && match[1]) {
          sceneNumber = match[1];
        }
      }
      
      // Extract INT/EXT info - check multiple possible field names
      let intExt = '';
      let locationType = scene.location_type || scene.intExt || scene.int_ext || '';
      
      if (locationType.includes('INT') && locationType.includes('EXT')) {
        intExt = 'I/E';
        locationType = 'INT/EXT';
      } else if (locationType.includes('INT') || locationType === 'I') {
        intExt = 'INT';
        locationType = 'INT';
      } else if (locationType.includes('EXT') || locationType === 'E') {
        intExt = 'EXT';
        locationType = 'EXT';
      }
      
      // Extract scene description
      let description = '';
      if (scene.description) {
        description = scene.description;
      } else if (scene.synopsis) {
        description = scene.synopsis;
      } else if (scene.action) {
        description = scene.action;
      } else if (scene.summary) {
        description = scene.summary;
      }
      
      // Extract scene location/setting
      let setScene = '';
      if (scene.setting) {
        setScene = scene.setting;
      } else if (scene.location) {
        setScene = scene.location;
      } else if (scene.slug_line) {
        setScene = scene.slug_line;
      } else if (scene.slugLine) {
        setScene = scene.slugLine;
      } else if (scene.slug) {
        setScene = scene.slug;
      }
      
      // Extract day/night info - check multiple possible field names
      let dayNight = '';
      
      // Check all possible field names for time of day
      if (scene.time) {
        dayNight = scene.time.toUpperCase();
      } else if (scene.timeOfDay) {
        dayNight = scene.timeOfDay.toUpperCase();
      } else if (scene.time_of_day) {
        dayNight = scene.time_of_day.toUpperCase();
      } else if (scene.dayNight) {
        dayNight = scene.dayNight.toUpperCase();
      } else if (scene.day_night) {
        dayNight = scene.day_night.toUpperCase();
      }
      
      // Try to extract from the setting/slugline if still no dayNight
      if (!dayNight && setScene) {
          // Try to extract from the setting/slugline
          if (setScene.includes('DAY')) dayNight = 'DAY';
          else if (setScene.includes('NIGHT')) dayNight = 'NIGHT';
          else if (setScene.includes('EVENING')) dayNight = 'EVENING';
          else if (setScene.includes('MORNING')) dayNight = 'MORNING';
          else if (setScene.includes('DAWN')) dayNight = 'DAWN';
          else if (setScene.includes('DUSK')) dayNight = 'DUSK';
          else if (setScene.includes('SUNSET')) dayNight = 'SUNSET';
          else if (setScene.includes('SUNRISE')) dayNight = 'SUNRISE';
          else if (setScene.includes('CONTINUOUS')) dayNight = 'CONTINUOUS';
          else if (setScene.includes('CONT.')) dayNight = 'CONT.';
          else if (setScene.includes('LATER')) dayNight = 'LATER';
          else if (setScene.includes('SAME TIME')) dayNight = 'SAME TIME';
          else if (setScene.includes('INT') && setScene.includes('EXT')) dayNight = 'I/E';
          else if (setScene.includes('I/E')) dayNight = 'I/E';
          else if (setScene.includes('E/I')) dayNight = 'I/E';
          else if (setScene.includes('INT')) dayNight = 'INT';
          else if (setScene.includes('EXT')) dayNight = 'EXT';
      }
    
      // Format page count in industry-standard 1/8ths
      let pages = '';
      if (scene.pages) {
        pages = formatPagesInEighths(scene.pages);
      } else if (scene.page_count) {
        pages = formatPagesInEighths(scene.page_count);
      } else if (scene.page_length) {
        pages = formatPagesInEighths(scene.page_length);
      } else if (scene.length) {
        pages = formatPagesInEighths(scene.length);
      }
      
      // Create the standardized scene object
      return {
        setScene: setScene,
        sceneNumber: sceneNumber,
        description: description,
        cast: scene.characters ? scene.characters.join(', ') : '',
        dayNight: dayNight,
        pages: pages,
        location: setScene, // Use the processed setScene for location
        location_type: locationType // Use the processed locationType
      };
    } catch (formatError) {
      console.error('Error formatting scene:', formatError);
      // Try to create a basic formatted scene to prevent complete failure
      setError('Error formatting scene: ' + formatError.message);
      return {
        setScene: 'Unknown',
        sceneNumber: scene.id || 'Unknown',
        description: scene.description || 'No description available',
        cast: '',
        dayNight: 'D',
        pages: '',
        location: ''
      };
    }
  };
  const handleUpdateScheduleItem = (index, field, value) => {
    const updatedSchedule = [...schedule];
    updatedSchedule[index] = { ...updatedSchedule[index], [field]: value };
    setSchedule(updatedSchedule);
    onUpdate({ 
      schedule: updatedSchedule,
      nextDaySchedule: nextDayScenes
    });
  };

  const handleSceneClick = (index) => {
    setSelectedSceneIndex(index);
    setEditingScene({...schedule[index]});
    setEditDialogOpen(true);
  };

  const handleCloseEditDialog = () => {
    setEditDialogOpen(false);
    setEditingScene(null);
  };

  const handleSaveEditDialog = () => {
    if (selectedSceneIndex !== null && editingScene) {
      const updatedSchedule = [...schedule];
      updatedSchedule[selectedSceneIndex] = editingScene;
      setSchedule(updatedSchedule);
      onUpdate({ 
        schedule: updatedSchedule,
        nextDaySchedule: nextDayScenes
      });
      setSuccess('Scene updated successfully');
      setEditDialogOpen(false);
    }
  };

  const handleAddScheduleItem = () => {
    const updatedSchedule = [...schedule, { 
      setScene: '', 
      sceneNumber: '', 
      description: '', 
      cast: [], 
      dayNight: '', 
      pages: '',
      location: ''
    }];
    setSchedule(updatedSchedule);
    onUpdate({ 
      schedule: updatedSchedule,
      nextDaySchedule: nextDayScenes
    });
  };

  const handleDeleteScheduleItem = (index) => {
    const updatedSchedule = [...schedule];
    updatedSchedule.splice(index, 1);
    setSchedule(updatedSchedule);
    onUpdate({ 
      schedule: updatedSchedule,
      nextDaySchedule: nextDayScenes
    });
  };

  return (
    <Paper 
      elevation={0}
      sx={{ 
        ...(styles?.infoSection || {}),
        p: 4, 
        mb: 4, 
        borderRadius: 1,
        border: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
        backgroundColor: theme.palette.background.paper
      }}
    >
      <Box sx={{ 
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        mb: 2,
        pb: 2,
        borderBottom: '1px solid',
        borderColor: 'divider'
      }}>
        <Typography 
          variant="h4" 
          component="h2"
          sx={{ 
            display: 'flex', 
            alignItems: 'center',
            fontWeight: 600,
            letterSpacing: '-0.25px',
            fontSize: '2rem',
            color: theme.palette.text.primary
          }}
        >
          <AccessTimeIcon 
            sx={{ 
              mr: 2,
              fontSize: '2rem',
              color: 'primary.main'
            }} 
          />
          Day {currentShootingDay} Scene Schedule
        </Typography>
      </Box>
      
      <Box sx={{ mb: 3 }}>
        {error && (
          <Alert 
            severity="error" 
            sx={{ 
              mb: 2,
              borderRadius: 1
            }} 
            onClose={() => setError(null)}
          >
            {error}
          </Alert>
        )}
        {success && (
          <Alert 
            severity="success" 
            sx={{ 
              mb: 2,
              borderRadius: 1
            }} 
            onClose={() => setSuccess(null)}
          >
            {success}
          </Alert>
        )}
        {loadingDayScenes ? (
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            gap: 2,
            p: 2,
            borderRadius: 1,
            backgroundColor: theme.palette.mode === 'dark'
              ? alpha(theme.palette.info.dark, 0.1)
              : alpha(theme.palette.info.light, 0.1),
          }}>
            <CircularProgress size={20} thickness={4} />
            <Typography variant="body2" color="text.secondary">
              Loading scenes for Day {currentShootingDay}...
            </Typography>
          </Box>
        ) : schedule.length > 0 ? (
          <Box sx={{ 
            p: 2,
            borderRadius: 1,
            backgroundColor: theme.palette.mode === 'dark'
              ? alpha(theme.palette.success.dark, 0.1)
              : alpha(theme.palette.success.light, 0.1),
            display: 'flex',
            alignItems: 'center',
            gap: 1
          }}>
            <MovieIcon sx={{ color: theme.palette.success.main }} />
            <Typography variant="body2">
              {schedule.length} scene{schedule.length !== 1 ? 's' : ''} scheduled for Day {currentShootingDay}
            </Typography>
          </Box>
        ) : (
          <Box sx={{ 
            p: 2,
            borderRadius: 1,
            backgroundColor: theme.palette.mode === 'dark'
              ? alpha(theme.palette.info.dark, 0.1)
              : alpha(theme.palette.info.light, 0.1),
            display: 'flex',
            alignItems: 'center',
            gap: 1
          }}>
            <MovieIcon sx={{ color: theme.palette.info.main }} />
            <Typography variant="body2">
              No scenes scheduled. They will appear when the shooting day changes.
            </Typography>
          </Box>
        )}
      </Box>
      <TableContainer 
        component={Paper} 
        sx={{ 
          mt: 2,
          borderRadius: 1,
          overflow: 'hidden',
          backgroundColor: theme.palette.background.paper,
          border: `1px solid ${alpha(theme.palette.divider, 0.12)}`
        }}
      >
        <Table 
          sx={{ 
            minWidth: 650,
            '& .MuiTableCell-root': {
              borderColor: theme.palette.mode === 'dark' 
                ? alpha(theme.palette.divider, 0.1)
                : theme.palette.divider,
              py: 2,
              px: 2,
              '&:first-of-type': {
                pl: 3
              },
              '&:last-of-type': {
                pr: 3
              }
            },
            '& .MuiTableBody-root .MuiTableRow-root:last-child .MuiTableCell-root': {
              borderBottom: 0
            }
          }} 
          aria-label="shooting schedule table"
        >
          <TableHead>
            <TableRow>
              <TableCell sx={theme.palette.mode === 'dark' ? darkHeaderStyle : lightHeaderStyle}>
                Scene #
              </TableCell>
              <TableCell sx={theme.palette.mode === 'dark' ? darkHeaderStyle : lightHeaderStyle}>
                Int/Ext
              </TableCell>
              <TableCell sx={theme.palette.mode === 'dark' ? darkHeaderStyle : lightHeaderStyle}>
                Description
              </TableCell>
              <TableCell sx={theme.palette.mode === 'dark' ? darkHeaderStyle : lightHeaderStyle}>
                Cast
              </TableCell>
              <TableCell sx={theme.palette.mode === 'dark' ? darkHeaderStyle : lightHeaderStyle}>
                Time
              </TableCell>
              <TableCell sx={theme.palette.mode === 'dark' ? darkHeaderStyle : lightHeaderStyle}>
                Pages
              </TableCell>
              <TableCell sx={theme.palette.mode === 'dark' ? darkHeaderStyle : lightHeaderStyle}>
                Location
              </TableCell>
              <TableCell sx={theme.palette.mode === 'dark' ? darkHeaderStyle : lightHeaderStyle}>
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {Array.isArray(schedule) && schedule.length > 0 ? schedule.map((item, index) => {
              // Determine color based on day/night value - MATCH ONELINER EXACTLY
              const isDark = theme.palette.mode === 'dark';
              
              // Get the scene's time of day for color coding
              // Get the scene's time of day for color coding and display
              let timeOfDay = item.dayNight ? item.dayNight.toUpperCase() : '';
              let displayedTime = timeOfDay;

              // Handle CONTINUOUS and LATER scenes by inheriting the time of day from the previous scene
              if (timeOfDay.includes('CONT') || timeOfDay.includes('LATER')) {
                if (index > 0 && schedule[index - 1]) {
                  const prevTime = schedule[index - 1].dayNight ? schedule[index - 1].dayNight.toUpperCase() : '';
                  // Inherit time, but don't inherit from another CONT/LATER scene
                  if (!prevTime.includes('CONT') && !prevTime.includes('LATER')) {
                    displayedTime = prevTime;
                    // Also update timeOfDay for color coding
                    timeOfDay = prevTime;
                  } else {
                    // Fallback if previous is also special
                    displayedTime = 'CONT.'; 
                    timeOfDay = 'CONT.';
                  }
                } else {
                   displayedTime = 'CONT.'; // First scene can't be continuous, but as a fallback
                   timeOfDay = 'CONT.';
                }
              }
              
              // Get location type (INT/EXT) from location_type field
              const locationType = item.location_type?.toUpperCase() || '';
              const isInterior = locationType.includes('INT') || locationType === 'I';
              const isExterior = locationType.includes('EXT') || locationType === 'E';
              
              // Default colors
              let stripColor = isDark ? 'rgba(97, 97, 97, 0.07)' : 'rgba(97, 97, 97, 0.04)';
              let textColor = theme.palette.mode === 'dark' ? '#ffffff' : '#000000';
              let borderColor = isDark ? 'rgba(255,255,255,0.12)' : '#e0e0e0';
              
              // Special case for montages
              if (item.location_type === 'MONTAGE' || timeOfDay.includes('VARIOUS') || timeOfDay === 'VAR.') {
                stripColor = isDark ? 'rgba(103, 58, 183, 0.07)' : 'rgba(103, 58, 183, 0.04)';
                borderColor = isDark ? '#673AB7' : '#9575CD';
              }
              // Interior scenes
              else if (isInterior && !isExterior) {
                if (timeOfDay.includes('DAY') || timeOfDay === 'D') {
                  stripColor = isDark ? 'rgba(255, 255, 255, 0.07)' : 'rgba(255, 255, 255, 0.04)';
                  borderColor = isDark ? '#B0BEC5' : '#90A4AE';
                } else if (timeOfDay.includes('NIGHT') || timeOfDay === 'N') {
                  stripColor = isDark ? 'rgba(33, 150, 243, 0.15)' : 'rgba(33, 150, 243, 0.1)';
                  borderColor = isDark ? '#2196F3' : '#64B5F6';
                } else if (timeOfDay.includes('MORNING') || timeOfDay === 'M') {
                  stripColor = isDark ? 'rgba(0, 188, 212, 0.07)' : 'rgba(0, 188, 212, 0.04)';
                  borderColor = isDark ? '#00BCD4' : '#80DEEA';
                } else if (timeOfDay.includes('EVENING') || timeOfDay === 'E') {
                  stripColor = isDark ? 'rgba(96, 125, 139, 0.07)' : 'rgba(96, 125, 139, 0.04)';
                  borderColor = isDark ? '#607D8B' : '#90A4AE';
                }
              }
              // Exterior scenes
              else if (isExterior && !isInterior) {
                if (timeOfDay.includes('DAY') || timeOfDay === 'D') {
                  stripColor = isDark ? 'rgba(255, 193, 7, 0.1)' : 'rgba(255, 236, 179, 0.4)';
                  borderColor = isDark ? '#FFC107' : '#FFA000';
                } else if (timeOfDay.includes('NIGHT') || timeOfDay === 'N') {
                  stripColor = isDark ? 'rgba(103, 58, 183, 0.1)' : 'rgba(187, 222, 251, 0.4)';
                  borderColor = isDark ? '#673AB7' : '#9575CD';
                } else if (timeOfDay.includes('DUSK') || timeOfDay.includes('EVENING') || timeOfDay === 'E') {
                  stripColor = isDark ? 'rgba(255, 87, 34, 0.1)' : 'rgba(255, 204, 188, 0.4)';
                  borderColor = isDark ? '#FF5722' : '#E64A19';
                } else if (timeOfDay.includes('DAWN') || timeOfDay.includes('MORNING') || timeOfDay === 'M') {
                  stripColor = isDark ? 'rgba(255, 193, 7, 0.08)' : 'rgba(255, 248, 225, 0.4)';
                  borderColor = isDark ? '#FFC107' : '#FFA000';
                } 
              }
              // Interior/Exterior scenes
              else if (locationType.includes('I/E') || locationType.includes('INT/EXT')) {
                if (timeOfDay.includes('DAY') || timeOfDay === 'D') {
                  stripColor = isDark ? 'rgba(0, 150, 136, 0.07)' : 'rgba(0, 150, 136, 0.04)';
                  borderColor = isDark ? '#009688' : '#80CBC4';
                } else if (timeOfDay.includes('NIGHT') || timeOfDay === 'N') {
                  stripColor = isDark ? 'rgba(103, 58, 183, 0.07)' : 'rgba(103, 58, 183, 0.04)';
                  borderColor = isDark ? '#673AB7' : '#9575CD';
                }
              }
              // Special cases
              if (timeOfDay.includes('CONT') || timeOfDay === 'CONT.') {
                stripColor = isDark ? 'rgba(158, 158, 158, 0.07)' : 'rgba(158, 158, 158, 0.04)';
                borderColor = isDark ? '#9E9E9E' : '#BDBDBD';
              }
              
              return (
                <TableRow 
                  key={index} 
                  onClick={() => handleSceneClick(index)}
                  sx={{ 
                    backgroundColor: `${stripColor} !important`,
                    cursor: 'pointer',
                    border: selectedSceneIndex === index ? '2px solid #1976d2' : `1px solid ${borderColor}`,
                    borderLeft: `4px solid ${borderColor}`,
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                      backgroundColor: theme.palette.mode === 'dark'
                        ? alpha(theme.palette.primary.main, 0.1)
                        : alpha(theme.palette.primary.main, 0.05)
                    },
                    '& .MuiTableCell-root': {
                      borderBottom: 'none',
                      padding: '10px 8px'
                    }
                  }}
                >
                  <TableCell>
                    <TextField
                      size="small"
                      value={item.sceneNumber || ''}
                      onChange={(e) => {
                        e.stopPropagation();
                        const updatedSchedule = [...schedule];
                        updatedSchedule[index] = { ...item, sceneNumber: e.target.value };
                        setSchedule(updatedSchedule);
                        onUpdate({ schedule: updatedSchedule });
                      }}
                      onClick={(e) => e.stopPropagation()}
                      variant="standard"
                      inputProps={{ 
                        style: { 
                          textAlign: 'center', 
                          fontSize: '0.875rem',
                          fontWeight: 'bold',
                          fontFamily: 'Roboto Condensed, sans-serif'
                        } 
                      }}
                      sx={{ width: 50 }}
                    />
                  </TableCell>
                  <TableCell>
                    <Select
                      size="small"
                      value={item.location_type || ''}
                      onChange={(e) => {
                        e.stopPropagation();
                        const updatedSchedule = [...schedule];
                        updatedSchedule[index] = { ...item, location_type: e.target.value };
                        setSchedule(updatedSchedule);
                        onUpdate({ schedule: updatedSchedule });
                      }}
                      onClick={(e) => e.stopPropagation()}
                      variant="standard"
                      sx={{ 
                        fontSize: '0.875rem',
                        fontWeight: 'bold',
                        minWidth: 60,
                        '& .MuiSelect-select': { py: 0.5, textAlign: 'center' }
                      }}
                    >
                      <MenuItem value="">-</MenuItem>
                      <MenuItem value="INT">INT</MenuItem>
                      <MenuItem value="EXT">EXT</MenuItem>
                      <MenuItem value="I/E">I/E</MenuItem>
                    </Select>
                  </TableCell>
                  <TableCell>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        fontSize: '0.875rem',
                        lineHeight: '1.4',
                        color: theme.palette.text.primary
                      }}
                    >
                      {item.description ? 
                        (item.description.split(/[.!?]\s/)[0] + (item.description.match(/[.!?]/) ? item.description.match(/[.!?]/)[0] : '')) : 
                        'No description'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                      <CastList cast={item.cast} castMembers={castMembers} />
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Select
                      size="small"
                      value={item.dayNight || ''}
                      onChange={(e) => {
                        e.stopPropagation();
                        const updatedSchedule = [...schedule];
                        updatedSchedule[index] = { ...item, dayNight: e.target.value };
                        setSchedule(updatedSchedule);
                        onUpdate({ schedule: updatedSchedule });
                      }}
                      onClick={(e) => e.stopPropagation()}
                      variant="standard"
                      sx={{ 
                        fontSize: '0.875rem',
                        fontWeight: 'bold',
                        minWidth: 80,
                        '& .MuiSelect-select': { py: 0.5, textAlign: 'center' }
                      }}
                    >
                      <MenuItem value="">-</MenuItem>
                      <MenuItem value="DAY">DAY</MenuItem>
                      <MenuItem value="NIGHT">NIGHT</MenuItem>
                      <MenuItem value="MORNING">MORNING</MenuItem>
                      <MenuItem value="EVENING">EVENING</MenuItem>
                      <MenuItem value="DAWN">DAWN</MenuItem>
                      <MenuItem value="DUSK">DUSK</MenuItem>
                      <MenuItem value="SUNSET">SUNSET</MenuItem>
                      <MenuItem value="SUNRISE">SUNRISE</MenuItem>
                      <MenuItem value="CONTINUOUS">CONT.</MenuItem>
                      <MenuItem value="LATER">LATER</MenuItem>
                    </Select>
                  </TableCell>
                  <TableCell>
                    <TextField
                      size="small"
                      value={item.pages || ''}
                      onChange={(e) => {
                        e.stopPropagation();
                        const updatedSchedule = [...schedule];
                        updatedSchedule[index] = { ...item, pages: e.target.value };
                        setSchedule(updatedSchedule);
                        onUpdate({ schedule: updatedSchedule });
                      }}
                      onClick={(e) => e.stopPropagation()}
                      variant="standard"
                      inputProps={{ style: { textAlign: 'center', fontSize: '0.875rem' } }}
                      sx={{ width: 50 }}
                      placeholder="0/8"
                    />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.5 }}>
                      <FormControl fullWidth size="small">
                        <Select
                          value={item.location || ''}
                          onChange={(e) => {
                            const selectedLocationName = e.target.value;
                            const selectedLocation = locations.find(loc => loc.name === selectedLocationName);
                            
                            const updatedSchedule = [...schedule];
                            updatedSchedule[index] = {
                              ...item,
                              location: selectedLocationName,
                              locationAddress: selectedLocation?.address || ''
                            };
                            setSchedule(updatedSchedule);
                            onUpdate({ schedule: updatedSchedule });
                          }}
                          displayEmpty
                          sx={{
                            fontSize: '0.875rem',
                            fontFamily: 'Roboto, sans-serif',
                            '& .MuiSelect-select': {
                              py: 1
                            }
                          }}
                        >
                          <MenuItem value="">
                            <Typography color="text.secondary">Select location</Typography>
                          </MenuItem>
                          {(item.location && !locations.some(loc => loc.name === item.location)) && (
                            <MenuItem value={item.location}>
                              <Typography variant="body2">{item.location}</Typography>
                            </MenuItem>
                          )}
                          {locations.map((loc, i) => (
                            <MenuItem 
                              key={i} 
                              value={loc.name}
                            >
                              <Typography variant="body2">{loc.name}</Typography>
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                      {item.locationAddress && (
                        <Typography 
                          variant="caption" 
                          color="text.secondary"
                          sx={{ 
                            fontSize: '0.75rem',
                            fontStyle: 'italic',
                            mt: 0.5,
                            lineHeight: 1.2
                          }}
                        >
                          {item.locationAddress}
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell align="center">
                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                      <IconButton 
                        size="small" 
                        onClick={(e) => {
                          e.stopPropagation(); // Prevent row click
                          handleDeleteScheduleItem(index);
                        }}
                        sx={{ 
                          color: theme.palette.error.main,
                          opacity: 0.7,
                          '&:hover': {
                            opacity: 1,
                            backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)'
                          }
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </TableCell>
                </TableRow>
              );
            }) : (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 3 }}>
                  <Typography variant="body2" color="text.secondary">
                    No scenes in schedule. They will appear automatically when shooting day changes.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        mb: 4,
        p: 3,
        borderRadius: 1,
        border: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
        backgroundColor: theme.palette.background.paper
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Typography 
            variant="h5" 
            component="h2"
            sx={{ 
              display: 'flex', 
              alignItems: 'center',
              fontWeight: 600,
              letterSpacing: '-0.5px',
              fontSize: '1.75rem'
            }}
          >
            <MovieIcon 
              sx={{ 
                mr: 2,
                fontSize: '2rem',
                color: 'primary.main'
              }} 
            />
            Shooting Schedule
          </Typography>
        </Box>
        <Tooltip title="Add scene" arrow>
          <span>
            <IconButton
              onClick={handleAddScheduleItem}
              aria-label="Add scene"
              sx={{
                width: 40,
                height: 40,
                borderRadius: 1,
                border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
                color: theme.palette.primary.main,
                backgroundColor: 'transparent',
                '&:hover': {
                  backgroundColor: alpha(theme.palette.primary.main, 0.15)
                }
              }}
            >
              <AddIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2, textAlign: 'center' }}>
        Tip: List scenes in shooting order, not script order
      </Typography>

      {/* Advance Schedule Section */}
      {nextDayScenes.length > 0 && (
        <Box sx={{ mt: 4 }}>
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            mb: 4,
            p: 3,
            borderRadius: 1,
            border: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
            backgroundColor: theme.palette.background.paper
          }}>
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              <Typography 
                variant="h5" 
                component="h2"
                sx={{ 
                  display: 'flex', 
                  alignItems: 'center',
                  fontWeight: 600,
                  letterSpacing: '-0.5px',
                  fontSize: '1.75rem'
                }}
              >
                <CalendarTodayIcon 
                  sx={{ 
                    mr: 2,
                    fontSize: '2rem',
                    color: 'primary.main'
                  }} 
                />
                Advance Schedule - Day {currentShootingDay + 1}
              </Typography>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 1, ml: 6 }}>
                Preview of scenes for the next shooting day
              </Typography>
            </Box>
          </Box>

          <TableContainer 
            component={Paper} 
            sx={{ 
              mt: 4,
              borderRadius: 1,
              overflow: 'hidden',
              backgroundColor: theme.palette.background.paper,
              border: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
              ...styles?.table 
            }}
          >
            <Table 
              sx={{ 
                minWidth: 650,
                '& .MuiTableCell-root': {
                  borderColor: theme.palette.mode === 'dark' 
                    ? alpha(theme.palette.divider, 0.1)
                    : theme.palette.divider,
                  py: 2,
                  px: 2,
                  '&:first-of-type': {
                    pl: 3
                  },
                  '&:last-of-type': {
                    pr: 3
                  }
                },
                '& .MuiTableBody-root .MuiTableRow-root:last-child .MuiTableCell-root': {
                  borderBottom: 0
                }
              }} 
              aria-label="next day shooting schedule table"
            >
              <TableHead>
                <TableRow>
                  <TableCell sx={theme.palette.mode === 'dark' ? darkHeaderStyle : lightHeaderStyle}>
                    Scene #
                  </TableCell>
                  <TableCell sx={theme.palette.mode === 'dark' ? darkHeaderStyle : lightHeaderStyle}>
                    Int/Ext
                  </TableCell>
                  <TableCell sx={theme.palette.mode === 'dark' ? darkHeaderStyle : lightHeaderStyle}>
                    Description
                  </TableCell>
                  <TableCell sx={theme.palette.mode === 'dark' ? darkHeaderStyle : lightHeaderStyle}>
                    Cast
                  </TableCell>
                  <TableCell sx={theme.palette.mode === 'dark' ? darkHeaderStyle : lightHeaderStyle}>
                    Time
                  </TableCell>
                  <TableCell sx={theme.palette.mode === 'dark' ? darkHeaderStyle : lightHeaderStyle}>
                    Pages
                  </TableCell>
                  <TableCell sx={theme.palette.mode === 'dark' ? darkHeaderStyle : lightHeaderStyle}>
                    Location
                  </TableCell>
                  <TableCell sx={theme.palette.mode === 'dark' ? darkHeaderStyle : lightHeaderStyle} align="center">
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loadingNextDayScenes ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                      <CircularProgress size={24} sx={{ mr: 1 }} />
                      <Typography variant="body2" color="text.secondary" display="inline">
                        Loading next day's scenes...
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : nextDayScenes.length > 0 ? nextDayScenes.map((item, index) => {
                  // Determine color based on day/night value - MATCH MAIN SCHEDULE EXACTLY
                  const isDark = theme.palette.mode === 'dark';
                  
                  // Get the scene's time of day for color coding and display
                  let timeOfDay = item.dayNight ? item.dayNight.toUpperCase() : '';
                  let displayedTime = timeOfDay;

                  // Handle CONTINUOUS and LATER scenes by inheriting the time of day from the previous scene
                  if (timeOfDay.includes('CONT') || timeOfDay.includes('LATER')) {
                    if (index > 0 && nextDayScenes[index - 1]) {
                      const prevTime = nextDayScenes[index - 1].dayNight ? nextDayScenes[index - 1].dayNight.toUpperCase() : '';
                      // Inherit time, but don't inherit from another CONT/LATER scene
                      if (!prevTime.includes('CONT') && !prevTime.includes('LATER')) {
                        displayedTime = prevTime;
                        // Also update timeOfDay for color coding
                        timeOfDay = prevTime;
                      } else {
                        // Fallback if previous is also special
                        displayedTime = 'CONT.'; 
                        timeOfDay = 'CONT.';
                      }
                    } else {
                       displayedTime = 'CONT.'; // First scene can't be continuous, but as a fallback
                       timeOfDay = 'CONT.';
                    }
                  }
                  
                  // Get location type (INT/EXT) from location_type field
                  const locationType = item.location_type?.toUpperCase() || '';
                  const isInterior = locationType.includes('INT') || locationType === 'I';
                  const isExterior = locationType.includes('EXT') || locationType === 'E';
                  
                  // Color logic is working correctly - issue is visual/CSS related
                  
                  // Default colors
                  let stripColor = isDark ? 'rgba(97, 97, 97, 0.07)' : 'rgba(97, 97, 97, 0.04)';
                  let textColor = theme.palette.mode === 'dark' ? '#ffffff' : '#000000';
                  let borderColor = isDark ? 'rgba(255,255,255,0.12)' : '#e0e0e0';
                  
                  // Special case for montages
                  if (item.location_type === 'MONTAGE' || timeOfDay.includes('VARIOUS') || timeOfDay === 'VAR.') {
                    stripColor = isDark ? 'rgba(103, 58, 183, 0.07)' : 'rgba(103, 58, 183, 0.04)';
                    borderColor = isDark ? '#673AB7' : '#9575CD';
                  }
                  // Interior scenes
                  else if (isInterior && !isExterior) {
                    if (timeOfDay.includes('DAY') || timeOfDay === 'D') {
                      stripColor = isDark ? 'rgba(255, 255, 255, 0.07)' : 'rgba(255, 255, 255, 0.04)';
                      borderColor = isDark ? '#B0BEC5' : '#90A4AE';
                    } else if (timeOfDay.includes('NIGHT') || timeOfDay === 'N') {
                      stripColor = isDark ? 'rgba(33, 150, 243, 0.15)' : 'rgba(33, 150, 243, 0.1)';
                      borderColor = isDark ? '#2196F3' : '#64B5F6';
                    } else if (timeOfDay.includes('MORNING') || timeOfDay === 'M') {
                      stripColor = isDark ? 'rgba(0, 188, 212, 0.07)' : 'rgba(0, 188, 212, 0.04)';
                      borderColor = isDark ? '#00BCD4' : '#80DEEA';
                    } else if (timeOfDay.includes('EVENING') || timeOfDay === 'E') {
                      stripColor = isDark ? 'rgba(96, 125, 139, 0.07)' : 'rgba(96, 125, 139, 0.04)';
                      borderColor = isDark ? '#607D8B' : '#90A4AE';
                    }
                  }
                  // Exterior scenes - EXACT COPY FROM MAIN SCHEDULE
                  else if (isExterior && !isInterior) {
                    if (timeOfDay.includes('DAY') || timeOfDay === 'D') {
                      stripColor = isDark ? 'rgba(255, 193, 7, 0.1)' : 'rgba(255, 236, 179, 0.4)';
                      borderColor = isDark ? '#FFC107' : '#FFA000';
                    } else if (timeOfDay.includes('NIGHT') || timeOfDay === 'N') {
                      stripColor = isDark ? 'rgba(103, 58, 183, 0.1)' : 'rgba(187, 222, 251, 0.4)';
                      borderColor = isDark ? '#673AB7' : '#9575CD';
                    } else if (timeOfDay.includes('DUSK') || timeOfDay.includes('EVENING') || timeOfDay === 'E') {
                      stripColor = isDark ? 'rgba(255, 87, 34, 0.1)' : 'rgba(255, 204, 188, 0.4)';
                      borderColor = isDark ? '#FF5722' : '#E64A19';
                    } else if (timeOfDay.includes('DAWN') || timeOfDay.includes('MORNING') || timeOfDay === 'M') {
                      stripColor = isDark ? 'rgba(255, 193, 7, 0.08)' : 'rgba(255, 248, 225, 0.4)';
                      borderColor = isDark ? '#FFC107' : '#FFA000';
                    } 
                  }
                  // Fallback: If no specific interior/exterior detected but we have time info, assume exterior for day scenes
                  else if (!isInterior && !isExterior && (timeOfDay.includes('DAY') || timeOfDay === 'D')) {
                    stripColor = isDark ? 'rgba(255, 193, 7, 0.1)' : 'rgba(255, 236, 179, 0.4)';
                    borderColor = isDark ? '#FFC107' : '#FFA000';
                  }
                  // Fallback: If no specific interior/exterior detected but we have night time info, assume exterior
                  else if (!isInterior && !isExterior && (timeOfDay.includes('NIGHT') || timeOfDay === 'N')) {
                    stripColor = isDark ? 'rgba(103, 58, 183, 0.1)' : 'rgba(187, 222, 251, 0.4)';
                    borderColor = isDark ? '#673AB7' : '#9575CD';
                  }
                  // Interior/Exterior scenes
                  else if (locationType.includes('I/E') || locationType.includes('INT/EXT')) {
                    if (timeOfDay.includes('DAY') || timeOfDay === 'D') {
                      stripColor = isDark ? 'rgba(0, 150, 136, 0.07)' : 'rgba(0, 150, 136, 0.04)';
                      borderColor = isDark ? '#009688' : '#80CBC4';
                    } else if (timeOfDay.includes('NIGHT') || timeOfDay === 'N') {
                      stripColor = isDark ? 'rgba(103, 58, 183, 0.07)' : 'rgba(103, 58, 183, 0.04)';
                      borderColor = isDark ? '#673AB7' : '#9575CD';
                    }
                  }
                  // Special cases
                  if (timeOfDay.includes('CONT') || timeOfDay === 'CONT.') {
                    stripColor = isDark ? 'rgba(158, 158, 158, 0.07)' : 'rgba(158, 158, 158, 0.04)';
                    borderColor = isDark ? '#9E9E9E' : '#BDBDBD';
                  }
                  
                  // Color logic confirmed working - removed debug logs
                  
                  return (
                    <TableRow 
                      key={`next-day-${index}`} 
                      sx={{ 
                        backgroundColor: `${stripColor} !important`,
                        cursor: 'default',
                        borderRadius: '4px',
                        mb: 1,
                        overflow: 'hidden',
                        border: `1px solid ${borderColor}`,
                        borderLeft: `4px solid ${borderColor}`,
                        boxShadow: '0 1px 2px rgba(0,0,0,0.08)',
                        '& .MuiTableCell-root': {
                          borderBottom: 'none',
                          padding: '12px 16px'
                        },
                        '&:hover': {
                          backgroundColor: theme.palette.action.hover,
                          boxShadow: `0 0 8px ${alpha(borderColor, 0.3)}`,
                          transition: 'all 0.2s ease-in-out'
                        }
                      }}
                    >
                      <TableCell>
                        <TextField
                          size="small"
                          value={item.sceneNumber || ''}
                          onChange={(e) => {
                            const updatedNextDayScenes = [...nextDayScenes];
                            updatedNextDayScenes[index] = { ...item, sceneNumber: e.target.value };
                            setNextDayScenes(updatedNextDayScenes);
                            onUpdate({ schedule, nextDaySchedule: updatedNextDayScenes });
                          }}
                          variant="standard"
                          inputProps={{ 
                            style: { 
                              textAlign: 'center', 
                              fontSize: '0.875rem',
                              fontWeight: 'bold',
                              fontFamily: 'Roboto Condensed, sans-serif'
                            } 
                          }}
                          sx={{ width: 50 }}
                        />
                      </TableCell>
                      <TableCell>
                        <Select
                          size="small"
                          value={item.location_type || ''}
                          onChange={(e) => {
                            const updatedNextDayScenes = [...nextDayScenes];
                            updatedNextDayScenes[index] = { ...item, location_type: e.target.value };
                            setNextDayScenes(updatedNextDayScenes);
                            onUpdate({ schedule, nextDaySchedule: updatedNextDayScenes });
                          }}
                          variant="standard"
                          sx={{ 
                            fontSize: '0.875rem',
                            fontWeight: 'bold',
                            minWidth: 60,
                            '& .MuiSelect-select': { py: 0.5, textAlign: 'center' }
                          }}
                        >
                          <MenuItem value="">-</MenuItem>
                          <MenuItem value="INT">INT</MenuItem>
                          <MenuItem value="EXT">EXT</MenuItem>
                          <MenuItem value="I/E">I/E</MenuItem>
                        </Select>
                      </TableCell>
                      <TableCell>
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            fontSize: '0.875rem',
                            lineHeight: '1.4',
                            color: theme.palette.text.primary
                          }}
                        >
                          {item.description ? 
                            (item.description.split(/[.!?]\s/)[0] + (item.description.match(/[.!?]/) ? item.description.match(/[.!?]/)[0] : '')) : 
                            'No description'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                          <CastList cast={item.cast} castMembers={castMembers} />
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Select
                          size="small"
                          value={item.dayNight || ''}
                          onChange={(e) => {
                            const updatedNextDayScenes = [...nextDayScenes];
                            updatedNextDayScenes[index] = { ...item, dayNight: e.target.value };
                            setNextDayScenes(updatedNextDayScenes);
                            onUpdate({ schedule, nextDaySchedule: updatedNextDayScenes });
                          }}
                          variant="standard"
                          sx={{ 
                            fontSize: '0.875rem',
                            fontWeight: 'bold',
                            minWidth: 80,
                            '& .MuiSelect-select': { py: 0.5, textAlign: 'center' }
                          }}
                        >
                          <MenuItem value="">-</MenuItem>
                          <MenuItem value="DAY">DAY</MenuItem>
                          <MenuItem value="NIGHT">NIGHT</MenuItem>
                          <MenuItem value="MORNING">MORNING</MenuItem>
                          <MenuItem value="EVENING">EVENING</MenuItem>
                          <MenuItem value="DAWN">DAWN</MenuItem>
                          <MenuItem value="DUSK">DUSK</MenuItem>
                          <MenuItem value="SUNSET">SUNSET</MenuItem>
                          <MenuItem value="SUNRISE">SUNRISE</MenuItem>
                          <MenuItem value="CONTINUOUS">CONT.</MenuItem>
                          <MenuItem value="LATER">LATER</MenuItem>
                        </Select>
                      </TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          value={item.pages || ''}
                          onChange={(e) => {
                            const updatedNextDayScenes = [...nextDayScenes];
                            updatedNextDayScenes[index] = { ...item, pages: e.target.value };
                            setNextDayScenes(updatedNextDayScenes);
                            onUpdate({ schedule, nextDaySchedule: updatedNextDayScenes });
                          }}
                          variant="standard"
                          inputProps={{ style: { textAlign: 'center', fontSize: '0.875rem' } }}
                          sx={{ width: 50 }}
                          placeholder="0/8"
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.5 }}>
                          <FormControl fullWidth size="small">
                            <Select
                              value={item.location || ''}
                              onChange={(e) => {
                                const selectedLocationName = e.target.value;
                                const selectedLocation = locations.find(loc => loc.name === selectedLocationName);
                                const updatedNextDayScenes = [...nextDayScenes];
                                updatedNextDayScenes[index] = {
                                  ...item,
                                  location: selectedLocationName,
                                  locationAddress: selectedLocation?.address || ''
                                };
                                setNextDayScenes(updatedNextDayScenes);
                              }}
                              sx={{
                                fontSize: '0.875rem',
                                fontFamily: 'Roboto, sans-serif',
                                '& .MuiSelect-select': {
                                  py: 1
                                }
                              }}
                            >
                              <MenuItem value="">
                                <Typography color="text.secondary">Select location</Typography>
                              </MenuItem>
                              {(item.location && !locations.some(loc => loc.name === item.location)) && (
                                <MenuItem value={item.location}>
                                  <Typography variant="body2">{item.location}</Typography>
                                </MenuItem>
                              )}
                              {locations.map((loc, i) => (
                                <MenuItem 
                                  key={i} 
                                  value={loc.name}
                                  sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'flex-start'
                                  }}
                                >
                                  <Typography variant="body2">{loc.name}</Typography>
                                  {loc.address && (
                                    <Typography 
                                      variant="caption" 
                                      color="text.secondary"
                                      sx={{ mt: 0.5 }}
                                    >
                                      {loc.address}
                                    </Typography>
                                  )}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                          {item.locationAddress && (
                            <Typography 
                              variant="caption" 
                              color="text.secondary"
                              sx={{ 
                                fontSize: '0.75rem',
                                fontStyle: 'italic',
                                mt: 0.5,
                                lineHeight: 1.2
                              }}
                            >
                              {item.locationAddress}
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                          <IconButton 
                            size="small" 
                            onClick={(e) => {
                              e.stopPropagation(); // Prevent row click
                              // Handle delete for advanced schedule if needed
                              const updatedNextDayScenes = nextDayScenes.filter((_, i) => i !== index);
                              setNextDayScenes(updatedNextDayScenes);
                            }}
                            sx={{ 
                              color: theme.palette.error.main,
                              opacity: 0.7,
                              '&:hover': {
                                opacity: 1,
                                backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)'
                              }
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                }) : (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 3 }}>
                      <Typography variant="body2" color="text.secondary">
                        No scenes scheduled for the next shooting day.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}
    </Paper>
  );
};

export default Schedule;
